import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { NumberInput } from '../../ui/number-input';
import { DateTimeInput } from '../../ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../../../lib/dateInputs';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { cn } from '../../../lib/utils';

export interface RedistributeParams {
  new_lanes_count: number;
  allow_category_mixing: boolean;
  restart_time: string;
  new_heat_gap: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  params: RedistributeParams;
  onParamsChange: React.Dispatch<React.SetStateAction<RedistributeParams>>;
  /** Simulation renvoyée par le serveur (`null` tant qu'on n'a pas simulé). */
  preview: any | null;
  loading: boolean;
  onSimulate: () => void;
  onApply: () => void;
}

/**
 * Redistribution des vagues sur un nouveau nombre de lanes.
 *
 * Deux temps volontaires — *Simuler* puis *Appliquer* : l'opération réécrit
 * les horaires de tout l'événement, l'organisateur doit voir qui avance et qui
 * recule avant de valider. « Appliquer » reste désactivé tant qu'aucune
 * simulation n'a été faite.
 */
const RedistributeHeatsDialog: React.FC<Props> = ({
  open, onOpenChange, params, onParamsChange, preview, loading, onSimulate, onApply,
}) => {
  const { t } = useTranslation();
  const set = (patch: Partial<RedistributeParams>) => onParamsChange(prev => ({ ...prev, ...patch }));

  const impacted: any[] = preview?.impacted_teams || [];
  const advanced = impacted.filter(x => x.delay_minutes < 0).length;
  const delayed = impacted.filter(x => x.delay_minutes > 0).length;
  const unchanged = impacted.filter(x => x.delay_minutes === 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('heats.redistributeDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('heats.redistributeDialog.newLanes')}</Label>
              <NumberInput min={1} max={20}
                value={params.new_lanes_count}
                onChange={(e) => set({ new_lanes_count: parseInt(e.target.value) || 6 })}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('heats.redistributeDialog.heatInterval')}</Label>
              <NumberInput min={5} max={180} placeholder="Auto"
                value={params.new_heat_gap}
                onChange={(e) => set({ new_heat_gap: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">{t('heats.redistributeDialog.intervalHint')}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('heats.redistributeDialog.restartTime')}</Label>
            <DateTimeInput
              type="time"
              value={params.restart_time}
              onChange={(e) => set({ restart_time: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">{t('heats.redistributeDialog.restartTimeHint')}</p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="allow-mixing"
              checked={params.allow_category_mixing}
              onCheckedChange={(checked) => set({ allow_category_mixing: checked })}
            />
            <Label htmlFor="allow-mixing">{t('heats.redistributeDialog.allowMixing')}</Label>
          </div>

          {/* Preview */}
          {preview && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-medium">{t('heats.redistributeDialog.previewTitle')}</h4>

              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-muted rounded">
                  <div className="text-2xl font-bold">{preview.teams_excluded}</div>
                  <div className="text-xs text-muted-foreground">{t('heats.redistributeDialog.teamsExcluded')}</div>
                </div>
                <div className="text-center p-2 bg-muted rounded">
                  <div className="text-2xl font-bold">{preview.teams_redistributed}</div>
                  <div className="text-xs text-muted-foreground">{t('heats.redistributeDialog.teamsToRedistribute')}</div>
                </div>
                <div className="text-center p-2 bg-muted rounded">
                  <div className="text-2xl font-bold">{preview.old_heats_count}</div>
                  <div className="text-xs text-muted-foreground">{t('heats.redistributeDialog.heatsBefore')}</div>
                </div>
                <div className="text-center p-2 bg-muted rounded">
                  <div className="text-2xl font-bold text-foreground">{preview.new_heats_count}</div>
                  <div className="text-xs text-muted-foreground">{t('heats.redistributeDialog.heatsAfter')}</div>
                </div>
              </div>

              {/* Impact Summary */}
              <div className="flex gap-2 flex-wrap">
                {unchanged > 0 && (
                  <Badge variant="outline" className="text-muted-foreground">
                    {t('heats.redistributeDialog.unchanged', { count: unchanged })}
                  </Badge>
                )}
                {advanced > 0 && (
                  <Badge variant="outline" className="text-info">
                    {t('heats.redistributeDialog.advanced', { count: advanced })}
                  </Badge>
                )}
                {delayed > 0 && (
                  <Badge variant="outline" className="text-warning">
                    {t('heats.redistributeDialog.delayed', { count: delayed })}
                  </Badge>
                )}
              </div>

              {/* Teams Table */}
              {impacted.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('heats.redistributeDialog.teamColumn')}</TableHead>
                        <TableHead>{t('heats.redistributeDialog.categoryColumn')}</TableHead>
                        <TableHead>{t('heats.redistributeDialog.beforeColumn')}</TableHead>
                        <TableHead>{t('heats.redistributeDialog.afterColumn')}</TableHead>
                        <TableHead>{t('heats.redistributeDialog.impactColumn')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {impacted.slice(0, 20).map((team: any, idx: number) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{team.team_name}</TableCell>
                          <TableCell><Badge variant="secondary">{team.category_name}</Badge></TableCell>
                          <TableCell>{team.original_time || '-'}</TableCell>
                          <TableCell>{team.new_time || '-'}</TableCell>
                          <TableCell>
                            <span className={cn(
                              "font-medium",
                              team.delay_minutes < 0 && "text-info",
                              team.delay_minutes > 0 && "text-warning"
                            )}>
                              {team.delay_minutes === 0 ? '-'
                                : team.delay_minutes > 0 ? `+${team.delay_minutes} min` : `${team.delay_minutes} min`}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {impacted.length > 20 && (
                    <p className="text-xs text-muted-foreground p-2 text-center">
                      {t('heats.redistributeDialog.andMoreTeams', { count: impacted.length - 20 })}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="secondary" onClick={() => { if (refuseIfDateInputIncomplete(t)) return; onSimulate(); }} disabled={loading}>
            {loading && !preview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('heats.actions.simulate')}
          </Button>
          <Button
            variant="default"
            onClick={() => { if (refuseIfDateInputIncomplete(t)) return; onApply(); }}
            disabled={!preview || loading}
          >
            {loading && preview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {t('heats.actions.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RedistributeHeatsDialog;
